import { LibrarySymbolInfo, ResolutionString } from 'charting_library/datafeed-api'

import { getAnalyticsValues } from '@/features/chart/lib/config/chart-analytics-data'
import { CHART_TICK_RESOLUTION, CHART_TIMEFRAMES } from '@/lib/chart-timeframe'
import {
  type ChartSymbolIdentity,
  getChartExchangeCode,
} from '@/lib/tradingview/chart-symbol-identity'
import { extractBarAnalytics } from '@/lib/tradingview/datafeeds/candle-analytics'
import { AnalyticsBar, IChartDataProvider } from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  getChartMarketSession,
  toTradingViewCalendar,
} from '@/lib/tradingview/datafeeds/chart-market-session'
import {
  type ClosedMarketBarFilter,
  createClosedMarketBarFilter,
  dropsClosedMarketBars,
} from '@/lib/tradingview/datafeeds/closed-market-bars'
import { formatSymbol } from '@/lib/utils/format-util'
import { fetchChartData } from '@/service/chart.api'
import { Exchange } from '@/types/generated/common/types'
import type { CandleFamily, VenueSchedule } from '@/types/generated/public_api'

export type ChartInstrumentMetadata = {
  tickSize?: string
  displayName?: string
  description?: string
}

type ResolveInstrumentMetadata = (
  identity: ChartSymbolIdentity
) => ChartInstrumentMetadata | undefined | Promise<ChartInstrumentMetadata | undefined>

type ResolveVenueSchedule = (exchange: Exchange) => Promise<VenueSchedule | undefined>

/**
 * TV's pricescale/minmov from an instrument's tick size. The precision comes from the tick's
 * significant decimals, not the string's: venues send padded decimals (Upbit KRW-BTC ticks
 * "1000.0000000"), which read literally gave 7 decimals of trailing zeros on a 1,000-won tick.
 */
export function tickSizeToPriceScale(tickSize: string): { pricescale: number; minmov: number } {
  const fallback = { pricescale: 100, minmov: 1 }
  const numeric = Number(tickSize)
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback
  // Plain decimal text, also for exponent forms like "1e-7".
  const plain = /e/i.test(tickSize) ? numeric.toFixed(20) : tickSize.trim()
  const [integerPart, fractionPart = ''] = plain.split('.')
  const decimals = fractionPart.replace(/0+$/, '')
  if (!decimals) return { pricescale: 1, minmov: parseInt(integerPart, 10) || 1 }
  return {
    pricescale: Math.pow(10, decimals.length),
    minmov: parseInt(`${integerPart}${decimals}`, 10) || 1,
  }
}

export class ExchangeChartProvider implements IChartDataProvider {
  constructor(
    // Normally reads cached metadata synchronously so symbol resolution does
    // not block first paint. Callers may wait for metadata on venues such as
    // KRX where a native numeric code is not suitable as the chart label.
    private readonly resolveInstrumentMetadata?: ResolveInstrumentMetadata,
    // Holidays and shortened sessions for the symbol's venue. Awaited on
    // session-bound venues only: TradingView never re-resolves a symbol, so a
    // calendar that arrives late never reaches the chart.
    private readonly resolveVenueSchedule?: ResolveVenueSchedule
  ) {}

  getSupportedResolutions(): ResolutionString[] {
    // Tick (1T) first, like TV's own ordering; the datafeed serves it from the 1s bars.
    return [
      CHART_TICK_RESOLUTION,
      ...CHART_TIMEFRAMES.map((t) => t.resolution),
    ] as ResolutionString[]
  }

  async resolveSymbol(identity: ChartSymbolIdentity): Promise<LibrarySymbolInfo> {
    const metadata = await this.resolveInstrumentMetadata?.(identity)
    const tickSize = metadata?.tickSize
    const { pricescale, minmov } = tickSize
      ? tickSizeToPriceScale(tickSize)
      : { pricescale: 100, minmov: 1 }
    const exchangeKey = getChartExchangeCode(identity.exchange)
    const { symbol } = identity
    const isKrx = identity.exchange === Exchange.EXCHANGE_XKRX
    const marketSession = getChartMarketSession(identity.exchange)
    const displayName = (isKrx && (metadata?.displayName || metadata?.description)) || symbol
    const schedule =
      marketSession.session === '24x7'
        ? undefined
        : await this.resolveVenueSchedule?.(identity.exchange).catch(() => undefined)

    return {
      name: symbol,
      ticker: symbol,
      full_name: symbol,
      description: formatSymbol(displayName),
      ...(isKrx && metadata?.description ? { long_description: metadata.description } : {}),
      type: marketSession.type,
      session: marketSession.session,
      timezone: marketSession.timezone,
      ...toTradingViewCalendar(marketSession, schedule),
      exchange: exchangeKey,
      listed_exchange: exchangeKey,
      format: 'price',
      pricescale,
      minmov,
      has_intraday: true,
      has_empty_bars: false, // never synthesize candles for periods with no data
      has_daily: true,
      // Served directly: left to TV, 3D and 15S would be built from 1D / 1S bars on its own grid.
      daily_multipliers: ['1', '3'],
      has_weekly_and_monthly: true,
      has_seconds: true,
      seconds_multipliers: ['1', '15'],
      // Tick: 1s bars drawn as a continuous line (see CHART_TICK_TIMEFRAME).
      has_ticks: true,
      supported_resolutions: this.getSupportedResolutions(),
      volume_precision: 2,
      data_status: 'streaming',
    } as LibrarySymbolInfo
  }

  async getClosedMarketBarFilter(
    identity: ChartSymbolIdentity,
    timeframe: number
  ): Promise<ClosedMarketBarFilter | null> {
    if (!dropsClosedMarketBars(identity.exchange)) return null
    const schedule = await this.resolveVenueSchedule?.(identity.exchange).catch(() => undefined)
    return createClosedMarketBarFilter(schedule, timeframe)
  }

  async fetchBars(
    {
      identity,
      timeframe,
      from,
      to,
      limit,
      families = [],
    }: {
      identity: ChartSymbolIdentity
      timeframe: number
      from: number
      to: number
      limit?: number
      families?: CandleFamily[]
    },
    signal?: AbortSignal
  ): Promise<AnalyticsBar[]> {
    const data = await fetchChartData(
      {
        symbol: identity.symbol,
        exchange: identity.exchange,
        timeframe,
        from: from.toString(),
        to: to.toString(),
        limit,
        families: [...families],
      },
      signal
    )

    if (!data.candles) {
      return []
    }

    const { ticks, open, high, low, close, volumeBase } = data.candles
    // Route through the shared window-average implementation so this matches
    // the canonical aggregatedFundingRateAvgClose series exactly (positional
    // window, not a push/shift buffer that skips gaps).
    const fundingRateAvgClose = getAnalyticsValues(data.candles, 'aggregatedFundingRateAvgClose')

    const bars: AnalyticsBar[] = []
    for (let i = 0; i < ticks.length; i++) {
      const unixSec = Number(ticks[i])
      if (unixSec < from || unixSec > to) continue

      const { analytics, rawAnalytics } = extractBarAnalytics(data.candles, i)
      const avgClose = fundingRateAvgClose[i]

      bars.push({
        time: unixSec * 1000, // TV expects milliseconds
        open: open[i],
        high: high[i],
        low: low[i],
        close: close[i],
        volume: Math.round(volumeBase[i] * 100) / 100,
        analytics:
          typeof avgClose === 'number' && Number.isFinite(avgClose)
            ? { ...analytics, aggregatedFundingRateAvgClose: avgClose }
            : analytics,
        rawAnalytics,
      })
    }

    // TV requires bars in ascending order (oldest first); sort defensively in case API returns descending
    bars.sort((a, b) => a.time - b.time)

    return bars
  }
}
